import * as React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileText, Info } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  CitationChip,
  ConfidenceBar,
  formatConfidenceFormula,
  hasOntologyScores,
  OntologyScores,
  RecoveryConfidenceBreakdown,
  StatusPill,
  TabPanel,
  Tabs,
} from "@hp/ui";
import { ClaimStatusPill } from "../components/ClaimStatusPill";
import { ClaimValidatePanel } from "../components/ClaimValidatePanel";
import { getAgreementClause, getClaimCandidate, getCompanyDisallowance } from "../data/index";
import { buildPrecedentPack } from "../data/precedentPack";
import type { PrecedentPack } from "../data/types";
import { ClaimActionButtons } from "../components/ClaimActionButtons";
import { ClaimGroupDetailPanel } from "../components/claims/ClaimGroupDetailPanel";
import { ClaimGroupValueDisplay } from "../components/claims/ClaimGroupValueDisplay";
import { ClaimValueBreakdownPanel } from "../components/claims/ClaimValueBreakdownPanel";
import type { ClaimActionPreset } from "../lib/claimWorkflowActions";
import { DraftArgumentEditor } from "../components/DraftArgumentEditor";
import { ClaimChatPanel } from "../components/ClaimChatPanel";
import { OVERVIEW_ONLY } from "../lib/demoMode";
import { SourceDocumentViewer } from "../components/SourceDocumentViewer";
import type { AssistantSource } from "@hp/api-client";
import { ErrorState, LoadingState } from "../components/Feedback";
import {
  formatDate,
  formatDateTime,
  formatINR,
  formatValueRange,
  humanizeForum,
  humanizeIssueTag,
} from "../lib/workbenchFormat";

const DETAIL_TABS = [
  { id: "overview", label: "Overview" },
  { id: "value", label: "Value breakdown" },
  { id: "precedent", label: "Precedent" },
  { id: "documents", label: "Matching Clause" },
] as const;

type DetailTabId = (typeof DETAIL_TABS)[number]["id"];

interface ClaimDetailPageProps {
  /** Report drill-down: read-only, no accept/defer/reject or editing. */
  readOnly?: boolean;
  backTo?: string;
  backLabel?: string;
  actionPreset?: ClaimActionPreset;
}

export function ClaimDetailPage({
  readOnly = false,
  backTo = "/reports/potential",
  backLabel = "Potential Claims",
  actionPreset = "review",
}: ClaimDetailPageProps = {}) {
  const { id = "" } = useParams<{ id: string }>();
  const location = useLocation();
  // Inside the Overview's claims modal, an appeal-chain link should stay in the popup shell (its own
  // Close button, no nested sidebar/HP Genie) rather than break out into the full app.
  const inPopup = location.pathname.startsWith("/popup/");
  const disputeThreadPath = (findingId: string) =>
    inPopup ? `/popup/findings/${findingId}/dispute-thread` : `/findings/${findingId}/dispute-thread`;
  const disputeThreadBackState = { backTo: location.pathname, backLabel: backLabel };
  const [activeTab, setActiveTab] = React.useState<DetailTabId>("overview");

  const detailQuery = useQuery({
    queryKey: ["claim-candidates", id],
    queryFn: ({ signal }) => getClaimCandidate(id, signal),
    enabled: Boolean(id),
  });

  const candidate = detailQuery.data?.claim_candidate;

  const clauseQuery = useQuery({
    queryKey: ["agreement-clauses", candidate?.matched_clause_id],
    queryFn: ({ signal }) => getAgreementClause(candidate!.matched_clause_id as string, signal),
    enabled: Boolean(candidate?.matched_clause_id),
  });

  const reopeningClauseQuery = useQuery({
    queryKey: ["agreement-clauses", candidate?.reopening_clause_id],
    queryFn: ({ signal }) => getAgreementClause(candidate!.reopening_clause_id as string, signal),
    enabled: Boolean(candidate?.reopening_clause_id),
  });

  const disallowanceQuery = useQuery({
    queryKey: ["company-disallowances", candidate?.disallowance_id],
    queryFn: ({ signal }) => getCompanyDisallowance(candidate!.disallowance_id as string, signal),
    enabled: Boolean(candidate?.disallowance_id),
  });

  const [viewerSource, setViewerSource] = React.useState<AssistantSource | null>(null);

  const [pack, setPack] = React.useState<PrecedentPack | null>(null);
  const buildMutation = useMutation({
    mutationFn: () => buildPrecedentPack(id),
    onSuccess: (result) => setPack(result),
  });

  if (detailQuery.isLoading) return <LoadingState label="Loading claim candidate..." />;
  if (detailQuery.isError) return <ErrorState error={detailQuery.error} label="Could not load claim candidate" />;
  if (!detailQuery.data) return null;

  const {
    claim_candidate,
    finding,
    precedent_findings,
    authority_statuses,
    group_members = [],
    group_value_rollup = null,
    value_sources = [],
  } = detailQuery.data;
  const isGroupedClaim = group_members.length > 1;

  function authorityFor(findingId: string) {
    return authority_statuses.find((entry) => entry.finding_id === findingId) ?? null;
  }

  // Case numbers this page has a Finding row for, so a mention of one inside
  // a summary (e.g. "the case above it relies on") can open that finding's
  // own source document - a case outside this chain (e.g. the Supreme Court
  // appeal that put a precedent under review, which this page never fetched
  // as a Finding) has no id to open and stays plain text.
  const caseLinks = [finding, ...precedent_findings].map((f) => ({
    caseNumber: f.case_number,
    findingId: f.id,
    hasSourceDocument: !!f.has_source_document,
  }));
  // A finding with no ingested source document has nothing for the viewer
  // to open, so any mention of it (case number or citation) must stay
  // plain text rather than a link that opens to an empty state.
  const hasSourceById = new Map(caseLinks.map((l) => [l.findingId, l.hasSourceDocument]));

  type SummaryToken =
    | { kind: "link"; text: string; findingId: string }
    | { kind: "citation"; text: string; note: string; findingId: string };

  function summaryTokensFor(entry: {
    plain_summary_citations?: { quote: string; note: string; source_finding_id: string }[];
  }): SummaryToken[] {
    return [
      ...caseLinks
        .filter((l) => l.hasSourceDocument)
        .map((l): SummaryToken => ({ kind: "link", text: l.caseNumber, findingId: l.findingId })),
      ...(entry.plain_summary_citations ?? [])
        .filter((c) => hasSourceById.get(c.source_finding_id))
        .map(
          (c): SummaryToken => ({ kind: "citation", text: c.quote, note: c.note, findingId: c.source_finding_id }),
        ),
    ];
  }

  // Wraps case numbers and pinpoint-reference citations (a paragraph/
  // Article/Section number the summary names) as buttons that open that
  // finding's own source document - a citation to something outside this
  // chain stays plain text, since there's no finding id to open.
  function annotateSummary(text: string, tokens: SummaryToken[]): React.ReactNode {
    const found = tokens.filter((t) => t.text && text.includes(t.text));
    if (found.length === 0) return text;
    // Longest first, so e.g. "Appeal No. 113 of 2022" isn't half-matched
    // inside a longer combined citation before the full one is tried.
    const sorted = [...found].sort((a, b) => b.text.length - a.text.length);
    const pattern = new RegExp(
      `(${sorted.map((t) => t.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
      "g",
    );
    return text.split(pattern).map((part, i) => {
      const match = sorted.find((t) => t.text === part);
      if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;
      return (
        <button
          key={i}
          type="button"
          title={match.kind === "citation" ? match.note : "Open source document"}
          onClick={() => setViewerSource({ type: "finding", id: match.findingId, label: part })}
          className="cursor-pointer font-medium text-[var(--color-accent-content)] underline decoration-dotted underline-offset-2 hover:decoration-solid"
        >
          {part}
        </button>
      );
    });
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link to={backTo} className="text-xs font-medium text-[var(--color-accent-content)] hover:underline">
          ← Back to {backLabel}
        </Link>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-5">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-zinc-900">{humanizeIssueTag(claim_candidate.issue_tag)}</h1>
            <ClaimStatusPill
              status={claim_candidate.status}
              counselValidatedAt={claim_candidate.counsel_validated_at}
            />
            {claim_candidate.limitation_flag ? <Badge tone="warning">Limitation risk</Badge> : null}
          </div>
          <p className="mt-1 text-sm text-zinc-500">
            Based on{" "}
            <Link
              to={disputeThreadPath(finding.id)}
              state={disputeThreadBackState}
              className="font-mono font-medium tabular-nums text-[var(--color-accent-content)] hover:underline"
            >
              {finding.case_number}
            </Link>{" "}
            · {humanizeForum(finding.forum)} · {formatDate(finding.decision_date)}
          </p>
          <p className="mt-0.5 text-xs text-zinc-400">
            {finding.petitioner} v. {finding.respondent}
          </p>
          {finding.has_source_document ? (
            <button
              type="button"
              onClick={() => setViewerSource({ type: "finding", id: finding.id, label: finding.case_number })}
              className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden />
              View claim file
            </button>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            {isGroupedClaim && group_value_rollup ? (
              <ClaimGroupValueDisplay rollup={group_value_rollup} />
            ) : claim_candidate.value_high > 0 || claim_candidate.value_low > 0 ? (
              <>
                <div className="font-mono text-lg font-semibold tabular-nums text-zinc-900">
                  {formatINR(claim_candidate.value_high)}
                </div>
                <div className="text-xs text-zinc-500">
                  Total · {formatValueRange(claim_candidate.value_low, claim_candidate.value_high)}
                </div>
              </>
            ) : null}
            <div className="mt-1 flex items-center justify-end gap-1.5 text-xs text-zinc-500">
              <span>Confidence</span>
              {claim_candidate.confidence_breakdown ? (
                <span className="group relative inline-flex">
                  <Info className="h-3.5 w-3.5 cursor-help text-zinc-400" aria-hidden />
                  <span className="sr-only">{formatConfidenceFormula(claim_candidate.confidence_breakdown)}</span>
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute bottom-full left-0 z-50 mb-1.5 w-64 scale-95 rounded-md bg-zinc-900 px-2.5 py-1.5 text-left text-[0.6875rem] font-normal leading-snug text-white opacity-0 shadow-lg transition duration-100 group-hover:scale-100 group-hover:opacity-100"
                  >
                    {formatConfidenceFormula(claim_candidate.confidence_breakdown)}
                  </span>
                </span>
              ) : null}
              <ConfidenceBar value={Math.round(claim_candidate.confidence_score * 100)} />
              <span>· Recommended: {humanizeForum(claim_candidate.recommended_forum)}</span>
            </div>
          </div>
          {readOnly ? null : (
            <ClaimActionButtons candidate={claim_candidate} preset={actionPreset} size="md" />
          )}
        </div>
      </div>

      {claim_candidate.limitation_flag && claim_candidate.limitation_note ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">Limitation note: </span>
          {claim_candidate.limitation_note}
        </div>
      ) : null}

      {claim_candidate.status === "rejected" && claim_candidate.rejection_reason ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <span className="font-medium">Rejection reason: </span>
          {claim_candidate.rejection_reason}
        </div>
      ) : null}
      {claim_candidate.status === "deferred" && claim_candidate.rejection_reason ? (
        <div className="rounded-md border border-zinc-300 bg-zinc-50 px-4 py-3 text-sm text-zinc-700">
          <span className="font-medium">Deferred because: </span>
          {claim_candidate.rejection_reason}
        </div>
      ) : null}

      <Tabs items={[...DETAIL_TABS]} value={activeTab} onChange={(id) => setActiveTab(id as DetailTabId)} />

      <TabPanel id="overview" activeId={activeTab} className="flex flex-col gap-5">
        {actionPreset === "pipeline" ? (
          <ClaimValidatePanel
            claimId={claim_candidate.id}
            members={
              group_members.length > 0
                ? group_members
                : [
                    {
                      id: claim_candidate.id,
                      issue_tag: claim_candidate.issue_tag,
                      finding_case_number: finding.case_number,
                      finding_id: claim_candidate.finding_id,
                      financial_year: claim_candidate.financial_year,
                      value_low: claim_candidate.value_low,
                      value_high: claim_candidate.value_high,
                      value_breakdown: claim_candidate.value_breakdown,
                      status: claim_candidate.status,
                      counsel_validated_at: claim_candidate.counsel_validated_at,
                    },
                  ]
            }
          />
        ) : null}

        {claim_candidate.confidence_breakdown ? (
          <Card>
            <CardHeader>
              <CardTitle>Recovery confidence</CardTitle>
            </CardHeader>
            <CardBody>
              <RecoveryConfidenceBreakdown values={claim_candidate.confidence_breakdown} />
            </CardBody>
          </Card>
        ) : hasOntologyScores(claim_candidate) ? (
          <Card>
            <CardHeader>
              <CardTitle>Claim scores</CardTitle>
            </CardHeader>
            <CardBody>
              <OntologyScores values={claim_candidate} />
            </CardBody>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Value Basis</CardTitle>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-zinc-700">{claim_candidate.value_basis}</p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Draft Argument</CardTitle>
          </CardHeader>
          <CardBody>
            {readOnly ? (
              <p className="whitespace-pre-wrap text-sm text-zinc-700">
                {claim_candidate.draft_argument ?? "No draft argument generated yet."}
              </p>
            ) : (
              <DraftArgumentEditor
                claimCandidateId={claim_candidate.id}
                draftArgument={claim_candidate.draft_argument}
                draftArgumentOriginal={claim_candidate.draft_argument_original}
              />
            )}
          </CardBody>
        </Card>

        {readOnly && !OVERVIEW_ONLY ? null : (
          <Card>
            <CardHeader>
              <CardTitle>Ask About This Claim</CardTitle>
            </CardHeader>
            <CardBody>
              <ClaimChatPanel claimCandidateId={claim_candidate.id} />
            </CardBody>
          </Card>
        )}
      </TabPanel>

      <TabPanel id="value" activeId={activeTab} className="flex flex-col gap-5">
        {isGroupedClaim ? (
          <ClaimGroupDetailPanel
            groupMembers={group_members}
            groupValueRollup={group_value_rollup}
            valueSources={value_sources}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Value breakdown</CardTitle>
            </CardHeader>
            <CardBody>
              <ClaimValueBreakdownPanel
                valueLow={claim_candidate.value_low}
                valueHigh={claim_candidate.value_high}
                breakdown={claim_candidate.value_breakdown}
              />
            </CardBody>
          </Card>
        )}

        {claim_candidate.disallowance_id ? (
          <Card>
            <CardHeader>
              <CardTitle>Originating Disallowance</CardTitle>
            </CardHeader>
            <CardBody>
              {disallowanceQuery.isLoading ? (
                <LoadingState label="Loading disallowance history..." />
              ) : disallowanceQuery.isError ? (
                <ErrorState error={disallowanceQuery.error} label="Could not load disallowance" />
              ) : disallowanceQuery.data ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                  <div>
                    <div className="text-xs text-zinc-400">Petition Ref</div>
                    <div className="font-mono font-medium tabular-nums text-zinc-900">
                      {disallowanceQuery.data.petition_ref}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-400">Claimed</div>
                    <div className="font-mono tabular-nums">{formatINR(disallowanceQuery.data.amount_claimed)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-400">Allowed</div>
                    <div className="font-mono tabular-nums">{formatINR(disallowanceQuery.data.amount_allowed)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-zinc-400">Filed</div>
                    <div>{formatDate(disallowanceQuery.data.filed_date)}</div>
                  </div>
                  <div className="col-span-2 sm:col-span-4">
                    <div className="text-xs text-zinc-400">Reason</div>
                    <div className="text-zinc-600">{disallowanceQuery.data.disallowance_reason}</div>
                  </div>
                </div>
              ) : null}
            </CardBody>
          </Card>
        ) : null}
      </TabPanel>

      <TabPanel id="precedent" activeId={activeTab} className="flex flex-col gap-5">
        <Card>
          <CardHeader>
            <CardTitle>Precedent chain ({precedent_findings.length + 1})</CardTitle>
            <p className="mt-1 text-xs text-zinc-500">
              Originating authority plus cited precedents resolved from citation edges and recorded chain ids.
            </p>
            <p className="mt-1 text-xs text-zinc-400">
              Ordered by legal reasoning, not date: each entry is the one the entry above it actually cites, tracing
              back through who relied on whom. A later-numbered entry can have an earlier decision date &mdash; that&apos;s
              expected, since old rulings get cited to support newer ones.
            </p>
          </CardHeader>
          <CardBody className="flex flex-col gap-0 p-0">
            <ul className="divide-y divide-zinc-100">
              <li className="bg-brand-50/40 px-4 py-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800">
                    1
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-accent-content)]">
                          Originating authority
                        </p>
                        <Link
                          to={disputeThreadPath(finding.id)}
              state={disputeThreadBackState}
                          className="font-mono text-sm font-medium tabular-nums text-[var(--color-accent-content)] hover:underline"
                        >
                          {finding.case_number}
                        </Link>
                        <div className="text-xs text-zinc-500">
                          {humanizeForum(finding.forum)} · {formatDate(finding.decision_date)} · {finding.outcome}
                        </div>
                        <p className="mt-0.5 text-xs text-zinc-400">
                          {finding.petitioner} v. {finding.respondent}
                        </p>
                      </div>
                      {authorityFor(finding.id) ? (
                        <StatusPill
                          kind="authority_status"
                          status={authorityFor(finding.id)!.status}
                          className="flex-none"
                          tooltipAlign="right"
                        />
                      ) : null}
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="min-w-0">
                        <p className="text-xs text-zinc-600">
                          {annotateSummary(finding.ratio_summary, summaryTokensFor(finding))}
                        </p>
                      </div>
                      <div className="min-w-0 border-t border-zinc-100 pt-3 md:border-l md:border-t-0 md:pl-6 md:pt-0">
                        <p className="text-[0.625rem] font-medium uppercase tracking-wide text-zinc-400">Summary</p>
                        <p className="mt-1 text-xs text-zinc-700">
                          {finding.plain_summary
                            ? annotateSummary(finding.plain_summary, summaryTokensFor(finding))
                            : "No additional plain-English detail available for this ruling."}
                        </p>
                        {finding.plain_summary_order_note ? (
                          <p className="mt-2 text-[0.6875rem] italic leading-snug text-amber-700">
                            <span className="font-semibold not-italic">Why this order:</span>{" "}
                            {annotateSummary(finding.plain_summary_order_note, summaryTokensFor(finding))}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
              {precedent_findings.length === 0 ? (
                <li className="px-4 py-3 text-sm text-zinc-400">No additional cited precedents in the resolved chain.</li>
              ) : (
                precedent_findings.map((entry, index) => {
                  const status = authorityFor(entry.id);
                  const recordedOnClaim = claim_candidate.precedent_chain.includes(entry.id);
                  return (
                    <li key={entry.id} className="px-4 py-4">
                      <div className="flex items-start gap-3">
                        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-zinc-100 text-xs font-semibold text-zinc-600">
                          {index + 2}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                                  Cited precedent
                                </p>
                                {recordedOnClaim ? <Badge tone="info">Recorded on claim</Badge> : null}
                              </div>
                              <Link
                                to={disputeThreadPath(entry.id)}
                                state={disputeThreadBackState}
                                className="font-mono text-sm font-medium tabular-nums text-[var(--color-accent-content)] hover:underline"
                              >
                                {entry.case_number}
                              </Link>
                              <div className="text-xs text-zinc-500">
                                {humanizeForum(entry.forum)} · {formatDate(entry.decision_date)} · {entry.outcome}
                              </div>
                              <p className="mt-0.5 text-xs text-zinc-400">
                                {entry.petitioner} v. {entry.respondent}
                              </p>
                            </div>
                            {status ? (
                              <StatusPill
                                kind="authority_status"
                                status={status.status}
                                className="flex-none"
                                tooltipAlign="right"
                              />
                            ) : null}
                          </div>
                          <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="min-w-0">
                              <p className="text-xs text-zinc-600">
                                {annotateSummary(entry.ratio_summary, summaryTokensFor(entry))}
                              </p>
                            </div>
                            <div className="min-w-0 border-t border-zinc-100 pt-3 md:border-l md:border-t-0 md:pl-6 md:pt-0">
                              <p className="text-[0.625rem] font-medium uppercase tracking-wide text-zinc-400">
                                Summary
                              </p>
                              <p className="mt-1 text-xs text-zinc-700">
                                {entry.plain_summary
                                  ? annotateSummary(entry.plain_summary, summaryTokensFor(entry))
                                  : "No additional plain-English detail available for this ruling."}
                              </p>
                              {entry.plain_summary_order_note ? (
                                <p className="mt-2 text-[0.6875rem] italic leading-snug text-amber-700">
                                  <span className="font-semibold not-italic">Why this order:</span>{" "}
                                  {annotateSummary(entry.plain_summary_order_note, summaryTokensFor(entry))}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })
              )}
            </ul>
          </CardBody>
        </Card>

        {OVERVIEW_ONLY ? null : (
          <Card>
            <CardHeader>
              <CardTitle>Precedent Pack</CardTitle>
              {readOnly ? null : (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => buildMutation.mutate()}
                  disabled={buildMutation.isPending}
                >
                  {buildMutation.isPending ? "Assembling..." : "Build Precedent Pack"}
                </Button>
              )}
            </CardHeader>
            <CardBody>
              {buildMutation.isPending ? (
                <LoadingState label="Assembling precedent pack from the resolved chain, clause, and disallowance record..." />
              ) : buildMutation.isError ? (
                <ErrorState error={buildMutation.error} label="Could not build precedent pack" />
              ) : pack ? (
                <div className="flex flex-col gap-3 text-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-zinc-900">{pack.headline}</h3>
                    <span className="text-xs text-zinc-400">Generated {formatDateTime(pack.generated_at)}</span>
                  </div>
                  <p className="text-zinc-700">{pack.finding_summary}</p>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      Cited Precedent ({pack.citation_count})
                    </h4>
                    {pack.precedent_lines.length === 0 ? (
                      <p className="mt-1 text-zinc-400">None cited.</p>
                    ) : (
                      <ul className="mt-1 list-disc space-y-1 pl-4 text-zinc-700">
                        {pack.precedent_lines.map((line, index) => (
                          <li key={index}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {pack.matched_clause_summary ? (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Matched Clause</h4>
                      <p className="mt-1 text-zinc-700">{pack.matched_clause_summary}</p>
                    </div>
                  ) : null}
                  {pack.disallowance_summary ? (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                        Originating Disallowance
                      </h4>
                      <p className="mt-1 text-zinc-700">{pack.disallowance_summary}</p>
                    </div>
                  ) : null}
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Value Basis</h4>
                    <p className="mt-1 text-zinc-700">{pack.value_basis}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Recommended Forum</h4>
                    <p className="mt-1 text-zinc-700">{humanizeForum(pack.recommended_forum)}</p>
                  </div>
                  {pack.limitation_note ? (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900">
                      {pack.limitation_note}
                    </div>
                  ) : null}
                  {pack.draft_argument ? (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Draft Argument</h4>
                      <p className="mt-1 whitespace-pre-line text-zinc-700">{pack.draft_argument}</p>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-zinc-400">
                  Not built yet. Click "Build Precedent Pack" to assemble the finding, precedent chain, matched clause,
                  and draft argument into a single reviewable pack.
                </p>
              )}
            </CardBody>
            {pack ? (
              <CardFooter>
                <Button type="button" variant="ghost" size="sm" onClick={() => setPack(null)}>
                  Clear
                </Button>
              </CardFooter>
            ) : null}
          </Card>
        )}
      </TabPanel>

      <TabPanel id="documents" activeId={activeTab} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Matched Company Clause</CardTitle>
            </CardHeader>
            <CardBody>
              {!claim_candidate.matched_clause_id ? (
                <p className="text-sm text-zinc-400">No clause matched for this candidate.</p>
              ) : clauseQuery.isLoading ? (
                <LoadingState label="Loading matched clause..." />
              ) : clauseQuery.isError ? (
                <ErrorState error={clauseQuery.error} label="Could not load matched clause" />
              ) : clauseQuery.data ? (
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CitationChip label={clauseQuery.data.clause.clause_ref} verified />
                    <Badge tone="info">{clauseQuery.data.clause.category}</Badge>
                  </div>
                  <p className="text-zinc-600">"{clauseQuery.data.clause.text}"</p>
                  <div className="mt-1 border-t border-zinc-100 pt-2 text-xs text-zinc-500">
                    {clauseQuery.data.agreement.name} · {clauseQuery.data.agreement.counterparty} · effective{" "}
                    {formatDate(clauseQuery.data.agreement.effective_date)}
                  </div>
                  {clauseQuery.data.clause.has_source_document ? (
                    <button
                      type="button"
                      onClick={() =>
                        setViewerSource({
                          type: "agreement_clause",
                          id: clauseQuery.data.clause.id,
                          label: clauseQuery.data.clause.clause_ref,
                        })
                      }
                      className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
                    >
                      <FileText className="h-3.5 w-3.5" aria-hidden />
                      View source document
                    </button>
                  ) : null}
                </div>
              ) : null}
            </CardBody>
          </Card>

          {claim_candidate.reopening_clause_id ? (
            <Card>
              <CardHeader>
                <CardTitle>Reopening Mechanism</CardTitle>
              </CardHeader>
              <CardBody>
                <p className="mb-2 text-xs text-zinc-500">
                  The matched disallowance was already settled - this is the clause giving HP a procedural door to
                  reopen it, distinct from the clause above the claim is valued against.
                </p>
                {reopeningClauseQuery.isLoading ? (
                  <LoadingState label="Loading reopening clause..." />
                ) : reopeningClauseQuery.isError ? (
                  <ErrorState error={reopeningClauseQuery.error} label="Could not load the reopening clause" />
                ) : reopeningClauseQuery.data ? (
                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CitationChip label={reopeningClauseQuery.data.clause.clause_ref} verified />
                      <Badge tone="success">{reopeningClauseQuery.data.clause.category}</Badge>
                    </div>
                    <p className="text-zinc-600">"{reopeningClauseQuery.data.clause.text}"</p>
                    {reopeningClauseQuery.data.clause.has_source_document ? (
                      <button
                        type="button"
                        onClick={() =>
                          setViewerSource({
                            type: "agreement_clause",
                            id: reopeningClauseQuery.data.clause.id,
                            label: reopeningClauseQuery.data.clause.clause_ref,
                          })
                        }
                        className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50"
                      >
                        <FileText className="h-3.5 w-3.5" aria-hidden />
                        View source document
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </CardBody>
            </Card>
          ) : null}
        </div>
      </TabPanel>

      {viewerSource ? <SourceDocumentViewer source={viewerSource} onClose={() => setViewerSource(null)} /> : null}
    </div>
  );
}
